/**
 * UseMyPendingAttestationsQuery (F-1 follow-up).
 *
 * Wraps GET ``/api/v1/training/pending-secondary-attestations``. The
 * inbox is caller-scoped (server filters by ``principal.user_id``); we
 * still key on ``userId`` so cache entries don't leak across sign-out
 * / sign-in transitions (mirror of ``useMyTrainingInboxQuery``).
 *
 * 5-min ``staleTime`` — the sidebar badge fires this on every page
 * mount, and the action mutations (sign-off, evidence upload) already
 * invalidate the cache, so a longer stale window cuts refetches
 * without losing freshness in practice.
 */

import {useQuery} from '@tanstack/react-query';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useAppContext} from '@/matic/providers/AppProvider';
import {
  getMyTrainingApi,
  type PendingSecondaryAttestation,
} from '../api/my-training-api';
import {myTrainingKeys} from '../query-keys';

export function useMyPendingAttestationsQuery() {
  const {userId} = useAppContext();
  const token = useApiToken();
  const enabled = Boolean(token && userId);
  return useQuery<PendingSecondaryAttestation[]>({
    queryKey: myTrainingKeys.pendingAttestations(userId ?? ''),
    async queryFn() {
      return getMyTrainingApi(token).listPendingSecondaryAttestations();
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}
